import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { X, Download, Smartphone, Share, Plus, Monitor, Chrome } from "lucide-react";

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [deviceType, setDeviceType] = useState("desktop"); // ios, android, desktop
  const [showInstructions, setShowInstructions] = useState(false);
  const [forceShow, setForceShow] = useState(false);

  useEffect(() => {
    // Check if already installed as standalone
    const checkStandalone = () => {
      return window.matchMedia('(display-mode: standalone)').matches 
        || window.navigator.standalone === true
        || document.referrer.includes('android-app://');
    };

    if (checkStandalone()) {
      setIsInstalled(true);
      return;
    }

    // Detect device type
    const ua = navigator.userAgent;
    if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) {
      setDeviceType("ios");
    } else if (/android/i.test(ua)) {
      setDeviceType("android");
    } else {
      setDeviceType("desktop");
    }

    // Check if dismissed recently (within 24 hours)
    const dismissedAt = localStorage.getItem('pwa-prompt-dismissed-v2');
    if (dismissedAt) {
      const hoursAgo = (Date.now() - parseInt(dismissedAt)) / (1000 * 60 * 60);
      if (hoursAgo < 24) {
        console.log('[PWA] Prompt dismissed recently, will not auto-show');
        return;
      }
    }

    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      console.log('[PWA] beforeinstallprompt event received!');
      e.preventDefault();
      setDeferredPrompt(e);
      // Show prompt after a short delay
      setTimeout(() => {
        setShowPrompt(true);
      }, 2500);
    };

    // Listen for successful install
    const handleAppInstalled = () => {
      console.log('[PWA] App was installed!');
      setIsInstalled(true);
      setShowPrompt(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    // Show prompt after delay regardless of beforeinstallprompt
    // This ensures the prompt appears even on browsers that don't support the event
    const showTimer = setTimeout(() => {
      if (!checkStandalone()) {
        console.log('[PWA] Showing install prompt (timer triggered)');
        setShowPrompt(true);
      }
    }, 4000);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      clearTimeout(showTimer);
    };
  }, []);

  // Function to trigger native install (if available)
  const triggerNativeInstall = async () => {
    if (deferredPrompt) {
      try {
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        console.log('[PWA] User choice:', outcome);
        if (outcome === 'accepted') {
          setIsInstalled(true);
          setShowPrompt(false);
        }
        setDeferredPrompt(null);
      } catch (err) {
        console.error('[PWA] Install error:', err);
        setShowInstructions(true);
      }
    } else {
      setShowInstructions(true);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    setShowInstructions(false);
    localStorage.setItem('pwa-prompt-dismissed-v2', Date.now().toString());
  };

  // Don't render if installed
  if (isInstalled) return null;

  // Don't render prompt until it should be shown
  if (!showPrompt && !forceShow) return null;

  // Render instructions modal
  if (showInstructions) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
        <div className="bg-card border border-border rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in zoom-in-95 duration-300">
          {/* Header */}
          <div className="bg-gradient-to-r from-primary to-primary/80 px-5 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <Download className="w-6 h-6 text-white" />
                </div>
                <div className="text-white">
                  <h3 className="font-extrabold text-xl tracking-tight">AI MAIL</h3>
                  <p className="text-xs text-white/70 font-light">by TelcaVoIP • Istruzioni</p>
                </div>
              </div>
              <button 
                onClick={handleDismiss}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Instructions based on device type */}
          <div className="p-5 space-y-4">
            {deviceType === "ios" ? (
              <>
                <p className="text-sm text-muted-foreground">
                  Su Safari per iPhone/iPad, segui questi passaggi:
                </p>
                <div className="space-y-3">
                  <div className="flex items-center gap-4 p-3 bg-secondary/50 rounded-lg">
                    <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                      <Share className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">1. Tocca "Condividi"</p>
                      <p className="text-xs text-muted-foreground">L'icona con la freccia verso l'alto</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 p-3 bg-secondary/50 rounded-lg">
                    <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                      <Plus className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">2. "Aggiungi a Home"</p>
                      <p className="text-xs text-muted-foreground">Scorri e seleziona questa opzione</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 p-3 bg-secondary/50 rounded-lg">
                    <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                      <Smartphone className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">3. Conferma con "Aggiungi"</p>
                      <p className="text-xs text-muted-foreground">L'app apparirà sulla tua Home</p>
                    </div>
                  </div>
                </div>
              </>
            ) : deviceType === "android" ? (
              <>
                <p className="text-sm text-muted-foreground">
                  Su Chrome per Android, segui questi passaggi:
                </p>
                <div className="space-y-3">
                  <div className="flex items-center gap-4 p-3 bg-secondary/50 rounded-lg">
                    <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-primary font-bold">⋮</span>
                    </div>
                    <div>
                      <p className="font-medium text-sm">1. Tocca il menu (⋮)</p>
                      <p className="text-xs text-muted-foreground">I tre puntini in alto a destra</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 p-3 bg-secondary/50 rounded-lg">
                    <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                      <Download className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">2. "Installa app" o "Aggiungi a Home"</p>
                      <p className="text-xs text-muted-foreground">Cerca questa voce nel menu</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 p-3 bg-secondary/50 rounded-lg">
                    <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                      <Smartphone className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">3. Conferma l'installazione</p>
                      <p className="text-xs text-muted-foreground">L'icona apparirà nel tuo drawer</p>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm text-muted-foreground">
                  Su Chrome/Edge per desktop, cerca uno di questi metodi:
                </p>
                <div className="space-y-3">
                  <div className="flex items-center gap-4 p-3 bg-secondary/50 rounded-lg">
                    <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                      <Chrome className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">Opzione 1: Icona nella barra indirizzi</p>
                      <p className="text-xs text-muted-foreground">Cerca l'icona ⊕ o il computer con freccia</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 p-3 bg-secondary/50 rounded-lg">
                    <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-primary font-bold">⋮</span>
                    </div>
                    <div>
                      <p className="font-medium text-sm">Opzione 2: Menu del browser</p>
                      <p className="text-xs text-muted-foreground">⋮ → "Installa TelcaVoIP..."</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 p-3 bg-secondary/50 rounded-lg">
                    <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                      <Monitor className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">Opzione 3: Altri browser</p>
                      <p className="text-xs text-muted-foreground">Aggiungi ai preferiti per accesso rapido</p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="px-5 pb-5">
            <Button onClick={handleDismiss} className="w-full">
              Ho capito
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Main install banner - visible on both mobile and desktop
  return (
    <div 
      className="fixed bottom-4 left-4 right-4 z-50 animate-in slide-in-from-bottom duration-500 sm:left-auto sm:right-4 sm:max-w-[420px]"
      data-testid="pwa-install-prompt"
    >
      <div className="bg-card border border-border rounded-2xl shadow-2xl overflow-hidden">
        {/* Colorful header */}
        <div className="bg-gradient-to-r from-primary via-primary/90 to-primary/70 px-4 py-3 sm:px-5 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                <Smartphone className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
              </div>
              <div className="text-white">
                <h3 className="font-extrabold text-xl sm:text-2xl tracking-tight">AI MAIL</h3>
                <p className="text-xs sm:text-sm text-white/70 font-light">by TelcaVoIP</p>
              </div>
            </div>
            <button 
              onClick={handleDismiss}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors text-white"
              data-testid="pwa-dismiss-btn"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-4 sm:p-5">
          {/* Benefits */}
          <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-4 sm:mb-5">
            <div className="text-center p-2 sm:p-3 bg-green-500/10 rounded-xl">
              <div className="w-7 h-7 sm:w-8 sm:h-8 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-1 sm:mb-2">
                <span className="text-green-600 text-base sm:text-lg">⚡</span>
              </div>
              <span className="text-[10px] sm:text-xs font-medium text-green-700 dark:text-green-400">Accesso Rapido</span>
            </div>
            <div className="text-center p-2 sm:p-3 bg-blue-500/10 rounded-xl">
              <div className="w-7 h-7 sm:w-8 sm:h-8 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-1 sm:mb-2">
                <span className="text-blue-600 text-base sm:text-lg">📴</span>
              </div>
              <span className="text-[10px] sm:text-xs font-medium text-blue-700 dark:text-blue-400">Funziona Offline</span>
            </div>
            <div className="text-center p-2 sm:p-3 bg-purple-500/10 rounded-xl">
              <div className="w-7 h-7 sm:w-8 sm:h-8 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-1 sm:mb-2">
                <span className="text-purple-600 text-base sm:text-lg">🔔</span>
              </div>
              <span className="text-[10px] sm:text-xs font-medium text-purple-700 dark:text-purple-400">Notifiche Push</span>
            </div>
          </div>
          
          {/* Buttons */}
          <div className="flex gap-2 sm:gap-3">
            <Button
              onClick={triggerNativeInstall}
              className="flex-1 h-10 sm:h-12 text-sm sm:text-base font-semibold gap-2"
              data-testid="pwa-install-btn"
            >
              <Download className="w-4 h-4 sm:w-5 sm:h-5" />
              {deferredPrompt ? "Installa Ora" : "Come Installare"}
            </Button>
            <Button
              variant="outline"
              onClick={handleDismiss}
              className="h-10 sm:h-12 text-sm sm:text-base"
            >
              Più tardi
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
